// iPad/iOS keyboard compensation (sticky chrome — ExamHeader, AppNotice):
// WKWebView pans the LAYOUT viewport when the on-screen
// keyboard opens, so sticky/fixed headers ride off-screen. When the visual
// viewport reports a pan (offsetTop > 0), translate the header by that
// offset so it stays visually pinned. Gates make this a strict no-op
// everywhere else: no `window.visualViewport` OR a fine pointer → no
// listeners → exactly today's behavior; a header that isn't actually
// stuck (computed position not sticky/fixed) is never translated; and the
// pin engages only on a REAL keyboard (visual viewport shrunk beyond the
// threshold) — iOS reports transient offsetTops during scroll/overscroll
// that must not toggle the transform frame-by-frame (iPad row 9).
import { useEffect, useRef } from 'react';

/** Minimum layout-vs-visual height shrink (px) that reads as a keyboard. */
export const KEYBOARD_HEIGHT_THRESHOLD_PX = 100;

/** Pure keyboard heuristic: heights in layout-equivalent CSS px (pass
 *  visualViewport.height * scale so pinch-zoom never reads as a keyboard). */
export const isKeyboardViewport = (layoutHeight: number, visualHeight: number): boolean =>
  Number.isFinite(layoutHeight) &&
  Number.isFinite(visualHeight) &&
  layoutHeight - visualHeight > KEYBOARD_HEIGHT_THRESHOLD_PX;

/** Pure math for the pin: only a stuck header with a real pan gets a transform. */
export const computeViewportPinTransform = (
  offsetTop: number,
  headerIsStuck: boolean
): string | null =>
  headerIsStuck && Number.isFinite(offsetTop) && offsetTop > 0
    ? `translateY(${offsetTop}px)`
    : null;

export const useVisualViewportPin = <T extends HTMLElement>() => {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    const element = ref.current;
    if (!viewport || !element) return undefined;
    if (!window.matchMedia?.('(pointer: coarse)').matches) return undefined;

    let frame = 0;
    const apply = () => {
      frame = 0;
      // jsdom's clientHeight is 0 — innerHeight is the fallback reference.
      const layoutHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
      const keyboardOpen = isKeyboardViewport(
        layoutHeight,
        viewport.height * (viewport.scale || 1)
      );
      // Forced style resolve only while the keyboard gate is open — the
      // no-keyboard path (per-frame scroll/pinch events) does two cheap
      // height reads and nothing else.
      let transform: string | null = null;
      if (keyboardOpen) {
        const position = window.getComputedStyle(element).position;
        transform = computeViewportPinTransform(
          viewport.offsetTop,
          position === 'sticky' || position === 'fixed'
        );
      }
      // Dynamic measured value (law 6) — set imperatively, cleared when the
      // keyboard closes so the header owns zero styling at rest. will-change
      // rides only while engaged (compositing hint against per-frame raster).
      if (transform) {
        element.style.setProperty('will-change', 'transform');
        element.style.setProperty('transform', transform);
      } else {
        element.style.removeProperty('transform');
        element.style.removeProperty('will-change');
      }
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(apply);
    };

    viewport.addEventListener('resize', schedule);
    viewport.addEventListener('scroll', schedule);
    return () => {
      viewport.removeEventListener('resize', schedule);
      viewport.removeEventListener('scroll', schedule);
      if (frame) window.cancelAnimationFrame(frame);
      element.style.removeProperty('transform');
      element.style.removeProperty('will-change');
    };
  }, []);

  return ref;
};
